import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { TableColumn, TableAction } from 'src/app/shared/components/data-table/data-table.component';
import { BadgeColor } from 'src/app/shared/models/app.models';
import { environment } from 'src/environments/environment';
import { lastValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-vendors',
  templateUrl: './vendors.component.html',
  styleUrls: ['./vendors.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VendorsComponent implements OnInit {
  AppConstants = AppConstants;
  vendors: any[] = [];
  isLoading = false;
  isExporting = false;
  tableColumns: TableColumn[] = [];

  private _loaderService = inject(LoaderService);
  private apiService = inject(ApiService);
  private _router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private toasterService = inject(ToasterService);
  private dialog = inject(MatDialog);

  ngOnInit(): void {
    this.initializeTableColumns();
    this.fetchVendors();
  }

  private initializeTableColumns(): void {
    const actionIcons: TableAction[] = [
      {
        iconPath: 'assets/icons/eye-icon.svg',
        action: 'view',
        title: 'View'
      },
      {
        icon: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#532E60" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.5 11C10.7091 11 12.5 9.20914 12.5 7C12.5 4.79086 10.7091 3 8.5 3C6.29086 3 4.5 4.79086 4.5 7C4.5 9.20914 6.29086 11 8.5 11Z" stroke="#532E60" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M20 8V14" stroke="#532E60" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M23 11H17" stroke="#532E60" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`,
        action: 'invite',
        title: 'Invite'
      }
    ];

    this.tableColumns = [
      { field: 'vendorId', header: 'Vendor ID', type: 'text', sortable: true },
      { field: 'erpVendorId', header: 'ERP Vendor ID', type: 'text', sortable: true },
      { field: 'name', header: 'Name', type: 'text', sortable: true },
      { field: 'type', header: 'Type', type: 'badge', sortable: true },
      { field: 'status', header: 'Status', type: 'badge', sortable: true, badgeColor: (row: any) => this.getStatusBadgeColor(row.status) },
      { field: 'localOrForeign', header: 'Local / Foreign', type: 'badge', sortable: true },
      { field: 'actions', header: 'Actions', type: 'action', actions: actionIcons, sortable: false }
    ];
  }

  async fetchVendors(): Promise<void> {
    this.isLoading = true;
    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.GetAllVendorsInfo}`;
      const response: any = await lastValueFrom(this.apiService.getByUrl(url));

      if (!response?.isSuccess && !response?.IsSuccess) {
        const errorMsg = response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to fetch vendors';
        console.error('[Vendors] API Error:', errorMsg);
        this.vendors = [];
        this.cdr.markForCheck();
        return;
      }

      const data = response?.data ?? response?.Data ?? [];
      console.log('[Vendors] Fetched vendors data:', data);
      this.vendors = Array.isArray(data) ? data.map((v: any) => this.mapVendor(v)) : [];
      console.log('[Vendors] Fetched vendors data:', this.vendors);

      this.cdr.markForCheck();
    } catch (error) {
      console.error('[Vendors] Error fetching vendors:', error);
      this.vendors = [];
      this.cdr.markForCheck();
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
      this._loaderService.hideLoader();
    }
  }

  async exportVendors(): Promise<void> {
    if (this.isLoading || this.isExporting || !this.vendors.length) {
      return;
    }

    this.isExporting = true;
    this.cdr.markForCheck();
    try {
      // Export the complete source list, independent of table filtering and pagination.
      const vendors = [...this.vendors];
      const rows: Record<string, string>[] = [];
      // Limit concurrent requests while fetching fields absent from the list API.
      for (let offset = 0; offset < vendors.length; offset += 5) {
        const batch = await Promise.allSettled(vendors.slice(offset, offset + 5).map(async vendor => {
          const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.Admin_GetVendorDetailsByID}?vendorId=${encodeURIComponent(vendor.vendorId)}&section=Workflow`;
          const response: any = await lastValueFrom(this.apiService.getByUrl(url));
          const workflow = response?.data ?? response?.Data;
          if (!(response?.isSuccess || response?.IsSuccess) || !workflow) {
            throw new Error(`Failed to load workflow for vendor ${vendor.vendorId}`);
          }
          return this.mapVendorExportRow(vendor, workflow);
        }));
        for (const result of batch) {
          if (result.status === 'rejected') {
            throw result.reason;
          }
          rows.push(result.value);
        }
      }
      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet['!cols'] = [22, 22, 40, 20, 24, 16, 24, 80, 36]
        .map(wch => ({ wch }));
      worksheet['!autofilter'] = { ref: worksheet['!ref']! };
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Vendors');
      const date = new Date();
      const dateStamp = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      XLSX.writeFile(workbook, `Vendors_${dateStamp}.xlsx`);
    } catch (error) {
      console.error('[Vendors] Error exporting vendors:', error);
      this.toasterService.showError('Failed to export vendors. Please try again.', 'Export failed');
    } finally {
      this.isExporting = false;
      this.cdr.markForCheck();
    }
  }

  private mapVendorExportRow(vendor: any, workflow: any): Record<string, string> {
    const status = this.getVendorStatusDisplay(workflow.status ?? workflow.Status ?? vendor.status);
    const normalizedStatus = this.normalizeVendorStatus(status);
    const history = workflow.workflowHistory ?? workflow.WorkflowHistory ?? [];
    const comments = (Array.isArray(history) ? history : [])
      .filter((item: any) => this.normalizeVendorStatus(item.action ?? item.Action) === 'changerequest')
      .sort((a: any, b: any) =>
        new Date(b.actionDate ?? b.ActionDate ?? 0).getTime() - new Date(a.actionDate ?? a.ActionDate ?? 0).getTime())
      .map((item: any) => item.comments ?? item.Comments ?? '')
      .filter((comment: string) => comment.trim())
      .join('\n');
    const isChangeRequest = normalizedStatus === 'changerequest' || normalizedStatus === 'changerequested';
    const localOrForeign = String(vendor.localOrForeign).trim().toLowerCase();

    return {
      'Vendor ID': String(vendor.vendorId),
      'ERP ID': String(vendor.erpVendorId),
      'Name': vendor.name,
      'Type': vendor.type,
      'Status': status,
      'Local?': localOrForeign === 'local' ? 'Yes' : localOrForeign === 'foreign' ? 'No' : vendor.localOrForeign,
      // The workflow screen uses the same Vendor.Status field as the application.
      'Workflow Status': status,
      'Change Request Comments': comments || (isChangeRequest ? workflow.rejectionReason ?? workflow.RejectionReason ?? '' : ''),
      'Owner of the Workflow Stage': normalizedStatus === 'submitted'
        ? workflow.assignedToApproveDisplay || workflow.AssignedToApproveDisplay || workflow.assignedToEmail || workflow.AssignedToEmail || ''
        : ''
    };
  }

  private normalizeVendorStatus(status: any): string {
    return (status ?? '').toString().replace(/\s+/g, '').toLowerCase();
  }

  private getVendorStatusDisplay(status: any): string {
    const normalizedStatus = this.normalizeVendorStatus(status);
    if (normalizedStatus === 'changerequest' || normalizedStatus === 'changerequested') {
      return 'Change Request';
    }

    return (status ?? '').toString();
  }

  getStatusBadgeColor(status: any): BadgeColor {
    const normalizedStatus = this.normalizeVendorStatus(status);
    if (normalizedStatus === 'changerequest' || normalizedStatus === 'changerequested') {
      return 'yellow';
    }

    if (normalizedStatus === 'rejected' || normalizedStatus === 'cancelled') {
      return 'red';
    }

    if (normalizedStatus === 'approved' || normalizedStatus === 'active') {
      return 'green';
    }

    if (normalizedStatus === 'submitted') {
      return 'blue';
    }

    if (normalizedStatus === 'draft' || normalizedStatus === 'pending') {
      return 'orange';
    }

    return 'gray';
  }

  private mapVendor(v: any): any {
    return {
      vendorId: v.vendorID ?? v.VendorID ?? v.vendorId ?? '',
      erpVendorId: v.eRP_VendorID ?? v.ERP_VendorID ?? v.erp_VendorID ?? v.erpVendorId ?? v.erP_VendorID ?? '',
      name: v.fullName || v.FullName || v.name || v.Name || '',
      type: v.vendorType || v.VendorType || '',
      status: this.getVendorStatusDisplay(v.status || v.Status || ''),
      localOrForeign: v.localOrForeign || v.LocalOrForeign || '',
      email: v.email || v.Email || '',
      mobile: v.mobile ?? v.Mobile ?? '',
      vendorReference: v.vendorReference ?? v.VendorReference ?? '',
      createdBy: v.createdBy ?? v.CreatedBy ?? ''
    };
  }

  onTableActionClick(event: { action: string, row: any }): void {
    if (event.action === 'view') {
      this._router.navigate([`/${AppConstants.ROUTES.VENDORS}/${AppConstants.ROUTES.VENDOR_DETAILS}/${event.row.vendorId}`]);
    } else if (event.action === 'invite') {
      void this.confirmInviteVendor(event.row);
    }
  }

  async confirmInviteVendor(vendor: any): Promise<void> {
    const vendorName = vendor?.name || vendor?.vendorId || 'this vendor';
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '520px',
      data: {
        title: 'Send Invitation',
        message: `Are you sure you want to send an invitation to ${vendorName}?`,
        confirmText: 'Yes, Send',
        cancelText: 'Cancel'
      }
    });

    const confirmed = await lastValueFrom(dialogRef.afterClosed());
    if (!confirmed) {
      return;
    }

    await this.inviteVendor(vendor.vendorId);
  }

  async inviteVendor(vendorId: string): Promise<void> {
    this._loaderService.showLoader();
    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.VENDOR}/${AppConstants.ACTIONS.InviteVendor}`;
      const response: any = await lastValueFrom(this.apiService.postByUrl(url, { vendorId }));

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('Vendor invited successfully', 'Success');
      } else {
        const errorMsg = response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to invite vendor';
        this.toasterService.showError(errorMsg, 'Error');
      }
    } catch (error: any) {
      const errorMsg = error?.error?.errorMessageEn || error?.message || 'An error occurred while inviting vendor';
      this.toasterService.showError(errorMsg, 'Error');
    } finally {
      this._loaderService.hideLoader();
    }
  }
}
